import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  requestPhoneVerificationCall,
  verifyPhoneNumber,
} from "../../api/auth";
import {
  formatRussianPhone,
  normalizeRussianPhone,
  validateRussianPhone,
} from "../../utils/validation";
import "./PhoneVerificationField.css";

export const AUTHORIZATION_LAW_URL =
  "https://publication.pravo.gov.ru/document/0001202606260053";

export interface PhoneVerificationValue {
  phone: string;
  verificationToken: string | null;
}

interface PhoneVerificationFieldProps {
  disabled?: boolean;
  initialPhone?: string;
  onChange: (value: PhoneVerificationValue) => void;
  onError?: (message: string | null) => void;
  required?: boolean;
  testIdPrefix?: string;
}

const PhoneVerificationField: React.FC<PhoneVerificationFieldProps> = ({
  disabled = false,
  initialPhone = "+7",
  onChange,
  onError,
  required = true,
  testIdPrefix = "signup",
}) => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState(() => formatRussianPhone(initialPhone));
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [verifiedPhone, setVerifiedPhone] = useState<string | null>(null);
  const [verificationExpiresAt, setVerificationExpiresAt] = useState<number | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState<"request" | "verify" | null>(null);
  const [retrySeconds, setRetrySeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const updateError = useCallback(
    (message: string | null) => {
      setError(message);
      onError?.(message);
    },
    [onError],
  );

  useEffect(() => {
    if (retrySeconds <= 0) return;
    const timer = window.setTimeout(
      () => setRetrySeconds((seconds) => Math.max(0, seconds - 1)),
      1000,
    );
    return () => window.clearTimeout(timer);
  }, [retrySeconds]);

  const canonicalPhone = normalizeRussianPhone(phone);
  const isVerified = !!verificationToken && verifiedPhone === canonicalPhone;
  const isBusy = disabled || loading !== null;

  useEffect(() => {
    if (!verificationToken || !verificationExpiresAt) return;

    const expireVerification = () => {
      setVerificationId(null);
      setVerificationToken(null);
      setVerifiedPhone(null);
      setVerificationExpiresAt(null);
      setCode("");
      updateError(t("auth.phoneVerificationExpired"));
      onChange({ phone: canonicalPhone, verificationToken: null });
    };
    const remainingMs = verificationExpiresAt - Date.now();

    if (remainingMs <= 0) {
      expireVerification();
      return;
    }

    const timer = window.setTimeout(expireVerification, remainingMs);
    return () => window.clearTimeout(timer);
  }, [canonicalPhone, onChange, t, updateError, verificationExpiresAt, verificationToken]);

  const handlePhoneChange = (value: string) => {
    const formattedPhone = formatRussianPhone(value);
    setPhone(formattedPhone);
    setVerificationId(null);
    setVerificationToken(null);
    setVerifiedPhone(null);
    setVerificationExpiresAt(null);
    setCode("");
    setRetrySeconds(0);
    updateError(null);
    onChange({ phone: formattedPhone, verificationToken: null });
  };

  const handleRequestCall = useCallback(async () => {
    if (!validateRussianPhone(canonicalPhone).valid) {
      updateError(t("auth.invalidRussianPhone"));
      return;
    }

    setLoading("request");
    updateError(null);
    setCode("");
    // Drop the previous challenge before requesting a replacement so it can
    // never be reused if the provider request fails.
    setVerificationId(null);
    setVerificationToken(null);
    setVerifiedPhone(null);
    setVerificationExpiresAt(null);
    setRetrySeconds(0);
    onChange({ phone: canonicalPhone, verificationToken: null });

    try {
      const response = await requestPhoneVerificationCall(canonicalPhone);
      setVerificationId(response.verificationId);
      setRetrySeconds(response.retryAfter);
    } catch (err: any) {
      if (typeof err?.retryAfter === "number" && err.retryAfter > 0) {
        setRetrySeconds(Math.ceil(err.retryAfter));
      }
      updateError(
        err?.code === "PHONE_CALL_DAILY_LIMIT"
          ? t("auth.phoneCallDailyLimit")
          : err.message || t("auth.phoneCallFailed"),
      );
    } finally {
      setLoading(null);
    }
  }, [canonicalPhone, onChange, t, updateError]);

  const handleVerify = useCallback(async () => {
    if (!verificationId || !/^\d{4}$/.test(code)) {
      updateError(t("auth.invalidPhoneCode"));
      return;
    }

    setLoading("verify");
    updateError(null);

    try {
      const response = await verifyPhoneNumber(canonicalPhone, code, verificationId);
      setVerificationToken(response.verificationToken);
      setVerifiedPhone(canonicalPhone);
      setVerificationExpiresAt(Date.now() + response.expiresIn * 1000);
      onChange({
        phone: canonicalPhone,
        verificationToken: response.verificationToken,
      });
    } catch (err: any) {
      updateError(err.message || t("auth.phoneVerificationFailed"));
    } finally {
      setLoading(null);
    }
  }, [canonicalPhone, code, onChange, t, updateError, verificationId]);

  return (
    <div className="phone-verification-field">
      <label className="phone-verification-label" htmlFor={`${testIdPrefix}-phone`}>
        {t("auth.phone")}{required && <> <span aria-hidden="true">*</span></>}
      </label>
      <input
        id={`${testIdPrefix}-phone`}
        className="phone-verification-input"
        type="tel"
        value={phone}
        onChange={(event) => handlePhoneChange(event.target.value)}
        disabled={isBusy}
        maxLength={16}
        inputMode="tel"
        autoComplete="tel"
        placeholder="+7 999 999 99 99"
        aria-describedby={`${testIdPrefix}-phone-hint`}
        data-testid={`${testIdPrefix}-phone`}
        required={required}
      />
      <p id={`${testIdPrefix}-phone-hint`} className="phone-verification-hint">
        {t("auth.phoneLawHint")}{" "}
        <a href={AUTHORIZATION_LAW_URL} target="_blank" rel="noreferrer">
          {t("auth.phoneLawLink")}
        </a>
        .
      </p>

      {!verificationId && !isVerified && (
        <div className="phone-verification-explainer">
          <strong>{t("auth.phoneCallExplanationTitle")}</strong>
          <ol>
            <li>{t("auth.phoneCallExplanationStep1")}</li>
            <li>{t("auth.phoneCallExplanationStep2")}</li>
            <li>
              <strong>{t("auth.phoneCallExplanationStep3Lead")}</strong>{" "}
              {t("auth.phoneCallExplanationStep3Body")}
            </li>
          </ol>
          <p>
            <strong>{t("auth.phoneCallExplanationPrivacyLead")}</strong>{" "}
            {t("auth.phoneCallExplanationPrivacyBody")}
          </p>
        </div>
      )}

      {error && !onError && (
        <div className="phone-verification-error" role="alert">
          {error}
        </div>
      )}

      {isVerified ? (
        <div className="phone-verification-success" role="status">
          <span aria-hidden="true">✓</span>
          {t("auth.phoneVerified")}
        </div>
      ) : (
        <button
          type="button"
          className="phone-verification-action"
          onClick={handleRequestCall}
          disabled={isBusy || retrySeconds > 0}
          data-testid={`${testIdPrefix}-request-call`}
        >
          {loading === "request"
            ? t("auth.requestingPhoneCall")
            : retrySeconds > 0
              ? t("auth.resendPhoneCallIn", { seconds: retrySeconds })
              : verificationId
                ? t("auth.resendPhoneCall")
                : t("auth.requestPhoneCall")}
        </button>
      )}

      {verificationId && !isVerified && (
        <div className="phone-verification-code-block">
          <p className="phone-verification-call-hint">{t("auth.phoneCallHint")}</p>
          <div className="phone-verification-code-row">
            <input
              className="phone-verification-code-input"
              type="text"
              aria-label={t("auth.phoneCode")}
              placeholder={t("auth.phoneCode")}
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, "").slice(0, 4));
                updateError(null);
              }}
              disabled={isBusy}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{4}"
              maxLength={4}
              data-testid={`${testIdPrefix}-code`}
            />
            <button
              type="button"
              className="phone-verification-action phone-verification-action--verify"
              onClick={handleVerify}
              disabled={isBusy || code.length !== 4}
              data-testid={`${testIdPrefix}-verify`}
            >
              {loading === "verify" ? t("auth.verifyingPhone") : t("auth.verifyPhone")}
            </button>
          </div>
          <div className="phone-verification-troubleshooting">
            <strong>{t("auth.phoneCallTroubleshootingTitle")}</strong>
            <ul>
              <li>{t("auth.phoneCallTroubleshootingWait")}</li>
              <li>{t("auth.phoneCallTroubleshootingSettings")}</li>
              <li>{t("auth.phoneCallTroubleshootingRoaming")}</li>
              <li>{t("auth.phoneCallTroubleshootingRetry")}</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneVerificationField;
